import { z } from 'zod';

const identifier = '(?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*)';
export const SemverSchema = z
  .string()
  .regex(
    new RegExp(
      `^v?(0|[1-9][0-9]*)\\.(0|[1-9][0-9]*)\\.(0|[1-9][0-9]*)(?:-(${identifier}(?:\\.${identifier})*))?(?:\\+[0-9A-Za-z-]+(?:\\.[0-9A-Za-z-]+)*)?$`,
    ),
  );
function parts(value: string) {
  const normalized = SemverSchema.parse(value).replace(/^v/, '').split('+')[0]!;
  const [release, ...pre] = normalized.split('-');
  return { release: release!.split('.').map(BigInt), pre: pre.join('-').split('.').filter(Boolean) };
}
export function compareVersions(left: string, right: string): number {
  const a = parts(left);
  const b = parts(right);
  for (let i = 0; i < 3; i++) {
    if (a.release[i] !== b.release[i]) return a.release[i]! > b.release[i]! ? 1 : -1;
  }
  if (!a.pre.length || !b.pre.length) return a.pre.length === b.pre.length ? 0 : a.pre.length ? -1 : 1;
  for (let i = 0; i < Math.max(a.pre.length, b.pre.length); i++) {
    const x = a.pre[i];
    const y = b.pre[i];
    if (x === y) continue;
    if (x === undefined || y === undefined) return x === undefined ? -1 : 1;
    const nx = /^\d+$/.test(x);
    const ny = /^\d+$/.test(y);
    if (nx && ny) return BigInt(x) > BigInt(y) ? 1 : -1;
    if (nx !== ny) return nx ? -1 : 1;
    return x > y ? 1 : -1;
  }
  return 0;
}
export function isSecurityRelease(title: string, notes = ''): boolean {
  return /\[security\]/i.test(`${title}\n${notes}`);
}
export const UpdateAgentFields = { version: SemverSchema, sha256: z.string().regex(/^[a-f0-9]{64}$/) };
export const UpdateInputSchema = z.object({ version: SemverSchema }).strict();
export const UpdateRequestSchema = UpdateInputSchema.extend({
  requestedAt: z.iso.datetime(),
  requestedBy: z.string().min(1),
});
export const UpdateInfoSchema = z.object({
  latest: SemverSchema.nullable(),
  url: z.url().nullable(),
  notes: z.string().nullable(),
  security: z.boolean(),
  checkedAt: z.iso.datetime(),
  error: z.string().nullable(),
});
export type UpdateInfo = z.infer<typeof UpdateInfoSchema>;
export const AgentAutoUpdateStateSchema = z.enum([
  'latest',
  'updating',
  'waiting_job',
  'waiting_retry',
  'failed',
  'disabled',
  'offline',
]);
export type AgentAutoUpdateState = z.infer<typeof AgentAutoUpdateStateSchema>;
export const LauncherSyncStatusSchema = z.object({
  checkedAt: z.iso.datetime().nullable(),
  version: z.string().nullable(),
  error: z.string().nullable(),
});
export type LauncherSyncStatus = z.infer<typeof LauncherSyncStatusSchema>;
export const SystemVersionSchema = z.object({
  launcher: LauncherSyncStatusSchema.optional(),
  agentBundleVersion: SemverSchema.nullable().optional(),
  version: SemverSchema,
  commit: z.string().nullable(),
  update: UpdateInfoSchema.nullable(),
  /** 新しいリリースを自動で入れるか */
  autoDeploy: z.object({ enabled: z.boolean() }).optional(),
  agents: z.array(
    z.object({
      serverId: z.string(),
      version: z.string().nullable(),
      outdated: z.boolean(),
      autoUpdate: AgentAutoUpdateStateSchema.optional(),
    }),
  ),
});
export type SystemVersion = z.infer<typeof SystemVersionSchema>;
export const UpdateStatusSchema = z.object({
  version: SemverSchema,
  state: z.enum(['pending', 'running', 'succeeded', 'failed', 'rolled_back']),
  updatedAt: z.iso.datetime(),
  message: z.string(),
});
export type UpdateStatus = z.infer<typeof UpdateStatusSchema>;
export const AgentUpdateResultSchema = z.object({
  serverId: z.string(),
  version: SemverSchema.optional(),
  ok: z.boolean(),
  error: z.string().optional(),
});
export type AgentUpdateResult = z.infer<typeof AgentUpdateResultSchema>;
export const AgentUpdateAcceptedSchema = z.object({ serverId: z.string(), version: SemverSchema });
export type AgentUpdateAccepted = z.infer<typeof AgentUpdateAcceptedSchema>;
export const GitHubReleaseSchema = z.object({
  tag_name: SemverSchema,
  html_url: z.url(),
  name: z.string().nullable(),
  body: z.string().nullable(),
});
